import { useMemo } from 'react';
import { grantsOperation, webRolesOf } from '@/auth/userAccess';
import { getDefaultRole, isPortal, PORTAL_HOME_PATH, PORTAL_LABELS, type Portal, type RoleCode } from '@/routes/roles';
import { useGlobalState } from './useGlobalState';

export interface RoleApi {
  /** The active role's base portal: the shell it opens in. */
  portal: Portal;
  /** Every portal the active role reaches, base portal first: their routes are mounted. */
  portals: Portal[];
  /** The active role's code as the API returns it (e.g. `librarian`). */
  roleCode: RoleCode;
  /** The active role's name from the API (Roles.DisplayName); the portal's name in sample data. */
  roleLabel: string;
  /** The active role's icon name from the API (an MUI icon name), or null. */
  roleIcon: string | null;
  /** The active role's home page: its Fabrica web home; the portal's home in sample data. */
  home: string;
  /**
   * True when the active role is granted the Fabrica operation `key` (e.g. `Staff.Sensitive.View`).
   * Signed in with its operations unknown (/api/me failed): false. Sample data (no one signed in):
   * true. The API checks every call either way.
   */
  can: (key: string) => boolean;
  /** Switches to one of the person's roles, by its code. */
  setRole: (code: RoleCode) => void;
}

/**
 * The active role, held in the global store (see `src/global/globalState.ts`). Signed in, its
 * portal and home are its Fabrica web home (GET /api/me), its other portals follow its web menus,
 * and its name, icon and operations are Fabrica's. In sample data the active role is a portal name.
 */
export function useRole(): RoleApi {
  const { state, setActiveRole } = useGlobalState();
  const { access, menus, activeRole } = state;
  return useMemo(() => {
    const web = access ? webRolesOf(access, menus).find((r) => r.code === activeRole) : undefined;
    if (web) {
      const { operations } = web;
      return {
        portal: web.portal,
        portals: web.portals,
        roleCode: web.code,
        roleLabel: web.name,
        roleIcon: web.icon,
        home: web.home,
        can: (key: string) => grantsOperation(operations, key),
        setRole: setActiveRole,
      };
    }
    // Sample data (or a role with no web home): the portal the code names, else the default one.
    const portal = !access && isPortal(activeRole) ? activeRole : getDefaultRole();
    const held = access?.roles.find((r) => r.code === activeRole);
    return {
      portal,
      portals: [portal],
      roleCode: activeRole,
      roleLabel: held?.name ?? PORTAL_LABELS[portal],
      roleIcon: held?.icon ?? null,
      home: PORTAL_HOME_PATH[portal],
      can: (key: string) => !access || grantsOperation(held?.operations, key),
      setRole: setActiveRole,
    };
  }, [access, menus, activeRole, setActiveRole]);
}
